/**
 * How much to trust a filing status, and when oddits and the portal disagree.
 * @file src/web/features/admin/lib/portal-status.ts
 *
 * The portal is the source of truth for filed or not. Oddits keeps its own
 * record beside it, so every status says when the portal was last asked, goes
 * "unverified" after a day, and a disagreement between the two is said out
 * loud instead of one quietly winning. Pure; the clock is passed in.
 *
 * @llm-rule WHEN: Showing a return's status, or deciding whether it can be trusted
 * @llm-rule AVOID: Showing 'filed' or 'not filed' with no indication of when the portal last said so
 */
import { isFiled, filedClaimed, type FilingStatusRow } from './filing-status';

/** After this long without asking the portal, a status is only a memory. */
export const STALE_AFTER_HOURS = 24;

export interface PortalEntry {
  returnType: string;
  period: string;
  checkedAt: string | null;
  filed: boolean | null;
  arn: string | null;
  filedOn: string | null;
  /** When it was last uploaded to the portal from oddits (null: never). */
  uploadedAt?: string | null;
}

export type Freshness =
  | { state: 'fresh'; text: string }
  | { state: 'stale'; text: string }
  | { state: 'never'; text: string };

const when = (iso: string) => new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit' });

/** "Last checked …", or "Unverified, open a portal session" once it is a day old or was never asked. */
export function freshness(checkedAt: string | null | undefined, now: Date): Freshness {
  if (!checkedAt) return { state: 'never', text: 'Unverified, open a portal session' };
  const age = (now.getTime() - new Date(checkedAt).getTime()) / 3_600_000;
  return age > STALE_AFTER_HOURS
    ? { state: 'stale', text: `Unverified, open a portal session · last checked ${when(checkedAt)}` }
    : { state: 'fresh', text: `Last checked ${when(checkedAt)}` };
}

/**
 * A sentence when oddits's record and the portal's last answer disagree, else
 * null. Oddits saying filed against a portal that said not filed is the
 * serious one; an answer too old to trust (or never given) disagrees with
 * nothing, because it says nothing.
 */
export function disagreement(
  rows: (FilingStatusRow & { arn: string | null })[], portal: PortalEntry | undefined, now: Date,
): string | null {
  if (!portal || portal.filed === null || freshness(portal.checkedAt, now).state !== 'fresh') return null;
  const claimsFiled = rows.some((r) => isFiled(r) || filedClaimed(r));
  if (claimsFiled && !portal.filed) {
    return 'Oddits has this return as filed, but the portal says it is not filed. Trust the portal: check it there.';
  }
  if (portal.filed) {
    const mine = rows.filter((r) => r.status === 'filed' && r.arn);
    if (mine.length && portal.arn && !mine.some((r) => r.arn!.toUpperCase() === portal.arn!.toUpperCase())) {
      return `Oddits records ARN ${mine[0].arn}, but the portal shows ${portal.arn}.`;
    }
    if (!rows.some((r) => r.status === 'filed')) {
      return `The portal shows this return filed${portal.arn ? ` under ARN ${portal.arn}` : ''}; oddits has not recorded it yet.`;
    }
  }
  return null;
}
